<template>
  <div>

    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="gotoRoute(`/${module}`)"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn  style="float: right"
          class="capitalize ctrl-button right"
          :color="containsErrors ? 'grey-5' : 'ctrl-turquoise'"
          :label="`${$store.state.stringsStore.strings.action.invite}`"
          :disable="containsErrors"
          @click="saveChanges('POST')"
          icon-right="mail"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">

      <!-- Left column -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">

        <!-- Usergroup block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ $store.state.stringsStore.strings.module.users.single }}
            </p>
            <div class="card-block scroll">
              <!-- Email fields -->
              <q-field class="q-mb-md" v-for="(v, i) in $v.userEmails.$each.$iter" :key="i"
                :error="valData.userEmails[i].set && valData.userEmails[i].warning"
                :error-label="valData.userEmails[i].set ? valData.userEmails[i].message : null">
                <q-input type="text" v-model="userEmails[i]"
                  @input="validateField(`userEmails[${i}].email`, 'userEmails', i)"
                  @blur="valData.userEmails[i].set = true"
                  :float-label="`${$store.state.stringsStore.strings.email}`">
                </q-input>
              </q-field>
              <!-- Button: Add email field -->
              <q-btn  style="float: right"
                class="capitalize ctrl-button right"
                :label="`${$store.state.stringsStore.strings.module.users.invite.emailBtn}`"
                color="ctrl-turquoise"
                @click="addEmail()"
                icon-right="mail"
              />
            </div>
          </div>
        </div>

        <!-- Websites block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">{{ $store.state.stringsStore.strings.website }}</p>
            <div class="card-block scroll">
              <div class="list item-inset-delimiter">
                <!-- Each available website -->
                <div class="item q-pt-md q-pb-md q-pl-sm q-pr-sm delimiter"
                  v-for="website in $store.state.meStore.me.websites"
                  :key="website.name">
                  <div class="row">
                    <div class="col-6">
                      {{ website.name }}
                    </div>
                    <div class="col-6">
                      <q-toggle v-model="data.websites"
                        class="float-right"
                        :val="website.id" color="secondary"/>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Right column -->
      <div class="col-md-6 col-sm-12 col-xs-12">

        <!-- Search users block -->
        <div class="row q-mb-lg">
          <div class="full-width">
            <p class="card-title">
              {{ $store.state.stringsStore.strings.module.name.usergroups }}
            </p>
            <!-- Searchfield -->
            <div class="q-mb-md" style="flex-grow: 1">
              <q-input
                color="grey-5"
                v-model="searchTerm"
                @input="applyFilters"
                :placeholder="$store.state.stringsStore
                .strings.module.users.invite.list.filter.placeholder"
              />
            </div>
            <!-- Data table -->
            <div class="q-table">
              <vuetable
                ref="vuetable"
                class="table-block q-mb-md"
                :class="{ 'is-loading': isLoading }"
                :table-height="tableHeight"
                :api-url="'usergroup'"
                pagination-path
                :api-mode="true"
                :noDataTemplate="noDataString(responseData.multiple)"
                :css="css.table"
                :http-fetch="fetchData"
                :fields="columns"
                :row-class="selectable ? applyRowClass : null"
                @vuetable:loading="onLoading"
                @vuetable:load-success="onSuccess"
                @vuetable:load-error="onError"
                @vuetable:pagination-data="onPaginationData">
                <!-- Custom fields -->
                <div slot="name" slot-scope="props">
                  <div v-html="highlight(props.rowData.name, filters.name)"></div>
                </div>
                <div slot="member" slot-scope="props">
                  <q-toggle v-model="data.usergroups" :val="props.rowData.id" color="secondary"/>
                </div>
              </vuetable>
              <!-- Pagination -->
              <div class="row pagination-bottom" id="myPagination">
                <!-- Per page select -->
                <div class="col">
                  <div class="row">
                    <q-select
                      class="btn-compact select-small"
                      hide-underline
                      color="grey"
                      v-model="perPage"
                      :options="perPageOptions"
                    />
                  </div>
                </div>
                <div class="col right-margin">
                  <vuetable-pagination
                    :on-each-side="1"
                    :css="css.pagination"
                    @vuetable-pagination:change-page="onChangePage"
                    ref="pagination">
                  </vuetable-pagination>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import Vuetable from 'vuetable-2';
import _get from 'lodash.get';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';
import { email } from 'vuelidate/lib/validators';

import getString from '@/helpers/GetStrings';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import searchHighlight from '@/mixins/tables/searchHighlight';
import fetchData from '@/mixins/tables/fetchData';
import saveChanges from '@/mixins/detailViews/saveChanges';
import common from '../common';

export default {
  validations: {
    userEmails: {
      $each: {
        email,
      },
    },
  },
  mixins: [
    tableMixin,
    pagination,
    searchHighlight,
    fetchData,
    saveChanges,
    common,
  ],
  components: {
    Vuetable,
    VuetablePagination,
  },
  name: 'UsersInvite',
  data() {
    return {
      tableHeight: 'calc(100vh - 360px)',
      valData: {
        userEmails: [{
          set: false,
          warning: false,
          message: '',
        }],
      },
      userEmails: [''],
      selectable: false,
      dataLoaded: false,
      data: {
        su: false,
        websites: [],
        usergroups: [],
      },
      columns: [
        {
          name: '__slot:name',
          title: getString('name'),
          sortField: 'name',
          titleClass: 'text-left sortable vcol-xl',
          dataClass: 'text-left vcol-xl',
        },
        {
          name: '__slot:member',
          title: getString('member'),
          titleClass: 'text-left sortable vcol-xs',
          dataClass: 'text-left vcol-xs',
        },
      ],
      isLoading: false,
      filters: {
        name: '',
      },
    };
  },
  methods: {
    // Override success mixin
    onSuccess(response) {
      // Set loading to false
      this.isLoading = false;
      // Modify the response object to fit vuetable.
      response.data.data = response.data.usergroups;
      // For selectable tables, map row ids to an array.
    },

    addEmail() {
      this.valData.userEmails.push({
        set: false,
        warning: false,
        message: '',
      });

      this.userEmails.push('');
    },
    validateField(value, field, i) {
      // Only first email is required.
      if (this.userEmails[0] === '') {
        this.valData.userEmails[i].warning = true;
        this.valData.userEmails[i].message = this.getValidationString('email', 'required');
      } else if (!_get(this.$v, `userEmails.$each.$iter.${i}.email`)) {
        this.valData.userEmails[i].warning = true;
        this.valData.userEmails[i].message = this.getValidationString('email', 'pattern.email');
      } else {
        this.valData.userEmails[i].warning = false;
        this.valData.userEmails[i].message = '';
      }
      return this.valData.userEmails[i].warning;
    },
    getValidationString(field, error) {
      let message = _get(this.$store.state.stringsStore.strings.form.error, error);
      if (message.match('%possesivePronoun%')) {
        message = message
          .replace('%possesivePronoun%', `${this.$store.state.stringsStore.strings.possesivePronoun.other} `);
      }
      if (message.match('%input%')) {
        message = message
          .replace('%input%', field);
      }
      return message;
    },
    gotoRoute(path) {
      this.$router.push(path);
    },
    requestParams() {
      const usergroups = [];
      const websites = [];
      const emails = [];

      // Push a new query string for each usergroup present in the data.usergroups array.
      this.data.usergroups.forEach((item) => {
        usergroups.push(`usergroups[]=${item}`);
      });

      // Push a new query string for each email present in the userEmails array.
      this.userEmails.forEach((item) => {
        // Add non-empty emails
        if (item !== '') {
          emails.push(`email[]=${item}`);
        }
      });

      // Push a new query string for each website present in the data.websites array.
      this.data.websites.forEach((item) => {
        websites.push(`websites[]=${item}`);
      });

      // Join each field for the user PUT request. This string is returned by the function.
      return [
        emails.length ? emails.join('&') : null,
        usergroups.length ? usergroups.join('&') : null,
        websites.length ? websites.join('&') : null,
      ].filter(val => val !== null).join('&');
    },
  },
  computed: {
    containsErrors() {
      return this.$v.$invalid
        || this.data.usergroups.length === 0
        || this.data.websites.length === 0
        || this.userEmails[0] === '';
    },
    fullName() {
      return [this.data.name.firstname, this.data.name.surname].filter(val => val !== null).join(' ');
    },
  },
  filters: {
    checkEmpty(value) {
      return value === null || value === '' ? '-' : value;
    },
    capitalize(value) {
      if (!value) return '';
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  },
  mounted() {
    // Set a minimum of one active website.
    this.data.websites.push(this.$store.state.meStore.me.websites[0].id);
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables'
</style>
